import type React from 'react';
import { Fragment, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, UserPlus, Pencil, Save } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@ui/card.tsx';
import { Badge } from '@ui/badge.tsx';
import { Separator } from '@ui/separator.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar.tsx';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { CopyButton } from '@ui/copy-button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@ui/dialog.tsx';
import { cn } from '@r/utils/utils.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';
import { PTS_CONVERSION_TYPES } from '@r/utils/workspace-utils.ts';
import {
  useGetAdminWorkspace,
  useApiAdminusersGetCollection,
  adminWorkspaceConfigs,
  apiAdminworkspaceConfigsIdPatch,
  apiAdminworkspaceConfigsPost
} from '@r/api/generated/client.ts';
import type { UserUserCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { roleToI18nKey, type Role } from '@r/utils/roles.ts';
import { useAllConfigs, getConfigsFromResponse, findConfig } from '@r/hooks/use-all-configs.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useRoutes } from '@r/hooks/use-routes.ts';
import { linksAvailable } from '@r/utils/utils.ts';
import { WorkspaceMisconfiguredBanner } from '@admin/components/workspace-configuration/workspace-misconfigured-banner.tsx';
import { ConfigurationStatusBadge } from '@admin/components/workspace-configuration/configuration-status-badge.tsx';
import { useWorkspaceConfigurationQuery } from '@admin/components/workspace-configuration/use-workspace-configuration.ts';

const DOMAIN_CONFIGURATION_URL = '/settings/my-workspace/domain-configuration';
const EMAIL_CONFIGURATION_URL = '/settings/my-workspace/email-configuration';

const WORKSPACE_STATUS_STYLES: Record<string, { dot: string; label: string }> = {
  active: { dot: 'bg-emerald-600', label: 'workspace.status.active' },
  misconfigured: {
    dot: 'bg-amber-500',
    label: 'workspace.status.misconfigured'
  },
  draft: { dot: 'bg-muted-foreground', label: 'workspace.status.draft' }
};

const InfoRow = ({
  label,
  value,
  action
}: {
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-0">
    <span className="text-muted-foreground text-sm w-60 shrink-0">{label}</span>
    <span className="text-sm">{value ?? '—'}</span>
    {action && <span className="text-sm sm:ml-auto">{action}</span>}
  </div>
);

const CopyableLink = ({ href, label = href }: { href: string; label?: string }) => (
  <span className="flex items-center gap-2">
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-blue-600 hover:underline"
    >
      {label}
    </a>
    <CopyButton value={label} />
  </span>
);

/*
 * Valeur accompagnée de son statut d'authentification, une fois récupéré.
 */
const AuthenticatedValue = ({
  children,
  isValid,
  isLoading
}: {
  children: React.ReactNode;
  isValid?: boolean;
  isLoading: boolean;
}) => (
  <span className="flex flex-wrap items-center gap-3">
    {children}
    <ConfigurationStatusBadge isValid={isValid} isLoading={isLoading} />
  </span>
);

/*
 * Lien vers la page d'authentification, libellé selon l'état de la configuration.
 */
const AuthenticationLink = ({ to, isValid }: { to: string; isValid?: boolean }) => {
  const { t } = useTranslation();

  return (
    <Link to={to} className="text-primary hover:underline">
      {isValid === false
        ? t('workspace.configuration.link.start')
        : t('workspace.configuration.link.consult')}
    </Link>
  );
};

/*
 * Statut d'authentification du domaine et des e-mails, chargé seulement si l'utilisateur
 * a accès à au moins une des pages de configuration (ROLE_ADMIN et plus).
 */
const useWorkspaceAuthenticationStatus = () => {
  const allRoutes = useRoutes();
  const availableConfigurationUrls = linksAvailable(allRoutes.data, [
    DOMAIN_CONFIGURATION_URL,
    EMAIL_CONFIGURATION_URL
  ]);
  const canSeeDomainConfiguration = availableConfigurationUrls.includes(DOMAIN_CONFIGURATION_URL);
  const canSeeEmailConfiguration = availableConfigurationUrls.includes(EMAIL_CONFIGURATION_URL);
  const configurationQuery = useWorkspaceConfigurationQuery(
    canSeeDomainConfiguration || canSeeEmailConfiguration
  );

  return {
    canSeeDomainConfiguration,
    canSeeEmailConfiguration,
    isLoading: configurationQuery.isLoading,
    dnsConfiguration: configurationQuery.data?.dnsConfiguration,
    emailConfiguration: configurationQuery.data?.emailConfiguration
  };
};

const WorkspaceAccessSection = () => {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const { canSeeDomainConfiguration, dnsConfiguration, isLoading } =
    useWorkspaceAuthenticationStatus();

  const isSuperAdmin = getActiveWorkspaceUniversal(user?.visibleWorkspaces)?.roles?.includes(
    'ROLE_FIDDY_ADMIN'
  );
  const adminUrl = window.location.origin;
  const adminPreprodUrl =
    isSuperAdmin && workspace?.preprodDomain ? `https://${workspace.preprodDomain}` : null;
  const clientUrl = workspace?.domain ? `https://${workspace.domain}` : null;

  return (
    <div className="space-y-3">
      <h3 className="font-semibold">{t('workspace.section.access')}</h3>
      <Separator />
      <div className="space-y-3 pt-1">
        {adminUrl && (
          <InfoRow
            label={t('workspace.field.admin.url')}
            value={<CopyableLink href={adminUrl} />}
          />
        )}
        {adminPreprodUrl && (
          <InfoRow
            label={t('workspace.field.admin.preprod.url')}
            value={<CopyableLink href={adminPreprodUrl} />}
          />
        )}
        {clientUrl && (
          <InfoRow
            label={t('workspace.field.client.url')}
            value={
              <AuthenticatedValue isValid={dnsConfiguration?.isValid} isLoading={isLoading}>
                <CopyableLink href={clientUrl} />
              </AuthenticatedValue>
            }
            action={
              canSeeDomainConfiguration && (
                <AuthenticationLink
                  to={DOMAIN_CONFIGURATION_URL}
                  isValid={dnsConfiguration?.isValid}
                />
              )
            }
          />
        )}
      </div>
    </div>
  );
};

const WorkspaceEmailsSection = () => {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const { canSeeEmailConfiguration, emailConfiguration, isLoading } =
    useWorkspaceAuthenticationStatus();
  const adminWorkspace = useGetAdminWorkspace(workspace?.id?.toString(), {
    query: { enabled: Boolean(workspace?.id) }
  }).data;

  return (
    <div className="space-y-3">
      <h3 className="font-semibold">{t('workspace.section.emails')}</h3>
      <Separator />
      <div className="space-y-3 pt-1">
        <InfoRow
          label={t('workspace.field.default.sender')}
          value={
            adminWorkspace?.defaultEmailSender && (
              <AuthenticatedValue isValid={emailConfiguration?.isValid} isLoading={isLoading}>
                <CopyableLink
                  href={`mailto:${adminWorkspace.defaultEmailSender}`}
                  label={adminWorkspace.defaultEmailSender}
                />
              </AuthenticatedValue>
            )
          }
          action={
            canSeeEmailConfiguration && (
              <AuthenticationLink
                to={EMAIL_CONFIGURATION_URL}
                isValid={emailConfiguration?.isValid}
              />
            )
          }
        />
        <InfoRow
          label={t('workspace.field.default.recipient')}
          value={
            adminWorkspace?.defaultHelpRecipient && (
              <CopyableLink
                href={`mailto:${adminWorkspace.defaultHelpRecipient}`}
                label={adminWorkspace.defaultHelpRecipient}
              />
            )
          }
        />
      </div>
    </div>
  );
};

export const Workspace = () => {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const logic = workspace?.currentLogicWorkspace;
  const statusStyle = WORKSPACE_STATUS_STYLES[workspace?.status ?? 'draft'];
  const ptsConversionType = useGetConfigByType('core', 'pts-conversion-type')?.[0] ?? null;
  const conversionLabel =
    ptsConversionType === PTS_CONVERSION_TYPES.TYPOLOGY
      ? t('form.workspace.convert.method.choice.typology')
      : t('form.workspace.convert.method.choice.company');
  const formatDate = (date?: string) => (date ? format(new Date(date), 'dd/MM/yyyy') : '—');

  const displayName = useGetConfigByType('core', 'title')?.[0] ?? workspace?.name ?? '';

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [displayNameValue, setDisplayNameValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const allConfigsQuery = useAllConfigs();
  const configs = useMemo(
    () => getConfigsFromResponse(allConfigsQuery.data),
    [allConfigsQuery.data]
  );

  const displayNameConfigQuery = useQuery({
    queryKey: ['workspaceConfigs', 'core', 'title'],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({
        'config.type': 'core',
        'config.key': 'title'
      });
      return response.data?.[0] ?? null;
    }
  });

  const handleEditOpen = () => {
    setDisplayNameValue('');
    setIsEditOpen(true);
  };

  const handleSave = async () => {
    const value = displayNameValue.trim();
    if (!value) return;

    setIsSaving(true);
    try {
      const existing = displayNameConfigQuery.data;
      if (existing?.id) {
        await apiAdminworkspaceConfigsIdPatch(existing.id.toString(), {
          value
        });
      } else {
        const config = findConfig(configs, 'core', 'title');
        if (!config?.id) return;
        await apiAdminworkspaceConfigsPost({
          value,
          config: `/configs/${config.id}`
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
      await displayNameConfigQuery.refetch();
      setIsEditOpen(false);
      toast.success(t('admin.translations.edit.form.success'));
    } catch {
      toast.error(t('toast.error.label'));
    } finally {
      setIsSaving(false);
    }
  };

  const adminsQuery = useApiAdminusersGetCollection(
    { 'maxRole[]': ['ROLE_ADMIN'] as const, perPage: 30 },
    { query: { queryKey: ['workspace-admins'] } }
  );
  const admins = (adminsQuery.data?.data ?? []) as UserUserCollectionReadModel[];

  return (
    <Fragment>
      <div className="mb-10">
        <h1 className="text-4xl font-bold mb-2">{t('workspace.profile')}</h1>
      </div>

      <div className="mb-6 empty:hidden">
        <WorkspaceMisconfiguredBanner showAction={false} />
      </div>

      <Card className="w-full">
        <CardContent className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <Avatar size="sm">
              <AvatarImage src="" alt={displayName} />
              <AvatarFallback text={displayName} initials={true} />
            </Avatar>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold">{displayName || '—'}</h2>
                <button
                  type="button"
                  onClick={handleEditOpen}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
              {workspace?.status && (
                <Badge
                  variant="outline"
                  className="gap-1.5 font-normal border-muted-foreground/20 w-fit"
                >
                  <span
                    className={cn('size-1.5 shrink-0 rounded-full', statusStyle.dot)}
                    aria-hidden
                  />
                  {t(statusStyle.label)}
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="font-semibold">{t('workspace.section.general')}</h3>
              <Separator />
              <div className="space-y-3 pt-1">
                <InfoRow label={t('workspace.field.id')} value={workspace?.id} />
                <InfoRow label={t('workspace.field.name')} value={workspace?.name} />
              </div>
            </div>

            {logic && (
              <div className="space-y-3">
                <h3 className="font-semibold">{t('workspace.section.program')}</h3>
                <Separator />
                <div className="space-y-3 pt-1">
                  <InfoRow label={t('workspace.field.logic')} value={t(logic.logic?.name)} />
                  <InfoRow label={t('workspace.field.conversion')} value={conversionLabel} />
                  <InfoRow
                    label={t('form.workspace.operation.date.start')}
                    value={formatDate(logic.startAt)}
                  />
                  <InfoRow
                    label={t('form.workspace.operation.date.end')}
                    value={formatDate(logic.endAt)}
                  />
                </div>
              </div>
            )}

            <WorkspaceAccessSection />

            <WorkspaceEmailsSection />
          </div>
        </CardContent>
      </Card>

      <Card className="w-full mt-6">
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">{t('workspace.section.admins')}</h2>
            <Link
              to="/users/import"
              search={(prev) => ({ ...prev, openCreateContact: true })}
              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <UserPlus className="w-4 h-4" />
              {t('global.label.add')}
            </Link>
          </div>

          <div className="space-y-1">
            {admins.map((admin, index) => {
              const uw = admin.userWorkspaces?.[0];
              const roleLabel = uw?.maxRole ? t(roleToI18nKey(uw.maxRole as Role)) : '—';
              const isLast = index === admins.length - 1;

              return (
                <div key={admin.id}>
                  <Link
                    to="/users/$id"
                    params={{ id: String(admin.id) }}
                    className="flex items-center justify-between py-3 hover:bg-muted/50 transition-colors rounded-md px-2 -mx-2"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar size="nav">
                        <AvatarImage src={admin.avatar ?? ''} alt={admin.fullname} />
                        <AvatarFallback text={admin.fullname} initials={true} />
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{admin.fullname}</span>
                        <span className="text-xs text-muted-foreground">{roleLabel}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </Link>
                  {!isLast && <Separator />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <Button onClick={() => navigate({ to: '/settings' })}>
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.edit.title.modal.title')}</DialogTitle>
            <DialogDescription>{t('workspace.edit.title.modal.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="display-name-current" className="text-sm font-medium">
                {t('workspace.edit.title.modal.current')}
              </label>
              <Input
                id="display-name-current"
                value={displayName}
                readOnly
                className="bg-muted text-muted-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="display-name-new" className="text-sm font-medium">
                {t('workspace.edit.title.modal.new')}
              </label>
              <Input
                id="display-name-new"
                value={displayNameValue}
                onChange={(e) => setDisplayNameValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSave}
              disabled={isSaving || !displayNameValue.trim()}
              className="w-full"
            >
              <Save />
              {t('workspace.edit.title.modal.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};
